<script setup>
import {reactive, onMounted, computed, ref} from "vue";
import JsonCSV from 'vue-json-csv';
import {ApiWrapper} from "@/Utils/ApiWrapper";
import {activeComponentStore} from "@/Stores/ActiveComponentStore";
import {Permissions} from "@/Utils/Permissions";
import {ToastUtil} from "@/Utils/ToastUtil";

const compName = 'BatchCreateComponent';

const data = reactive({
  valid: true,
  dataLoading: true,
  isEditing: false,
  loading: false,
  search: '',
  newBatch: false,
  newBatchName: null,
  items: [],
  headers: [
    { title: 'Batch', key: 'title' },
    { title: 'Date Created', key: 'date' },
    { title: 'Count of Payments', key: 'count' },
    { title: 'Sum of Payments', key: 'sum' },
    { title: 'Status', key: 'isclosed'},
    { title: 'Actions', key: 'actions' }
  ],
  labels: {
    title: "Batch",
    date: "Date Created",
    count: "Count of Payments",
    sum: "Sum of Payments",
    actions: "Actions",
    id: "ID",
    sumFormatted: "Formatted Sum"
  },
  editedItem: {
    title: '',
    date: '',
    count: '',
    sum: '',
    actions: '',
    id: '',
    sumFormatted: ''
  },

  defaultItem: {
    title: '',
    date: '',
    count: '',
    sum: '',
    actions: '',
    id: '',
    sumFormatted: ''
  },

});

const form = ref(null);

const csvData = computed(()=>{
  // CSV File
  return data.items.map((item) => ({
    title: item.title,
    date: item.date,
    count: item.count,
    sum: item.sum,
    actions: item.actions
  }))
})

const editItem = (item) => {
  data.isEditing = true
  data.editedItem = JSON.parse(JSON.stringify(item))
}

const editBatches = () => {
  if (form.value.validate()) {
    data.dataLoading = true;
    ApiWrapper.post('payments', 'edit-batch', {
          editedItem: data.editedItem
        })
        .then((response) => {
          activeComponentStore.notifyComponents(['batches'],compName);
          getData();
          discardItem();
          ToastUtil.successMsg('Updated batch');
        })
        .catch((err) => {
          ToastUtil.error(err);
          data.dataLoading = false
        })
  }
}

const deleteItem = (item) => {
  let deleteBatch = confirm(
      'Are you sure you want to delete this item?'
  )
  if (deleteBatch === true) {
    data.dataLoading = true;
    item.isDeleting = true
    ApiWrapper.post('payments', 'delete-batch', {
          item: item
        })
        .then((response) => {
          activeComponentStore.notifyComponents(['batches'],compName);
          getData()
          ToastUtil.successMsg('Deleted batch');
        })
        .catch((err) => {
          ToastUtil.error(err);
          data.dataLoading = false;
        })
  }
}

const getData = () => {
  data.dataLoading = true;

  ApiWrapper.get('payments', 'get-batches','contextId=batchdashboard')
      .then((response) => {
        data.items = response.rows;
        data.dataLoading = false;
      })
      .catch((err) => {
        ToastUtil.error(err);
        data.dataLoading = false;
      })
}

const saveItem = () => {
  if (form.value.validate()) {
    data.dataLoading = true;
    ApiWrapper.post('payments', 'save-batch', {
          batchName: data.newBatchName
        })
        .then((response) => {
          activeComponentStore.notifyComponents(['batches'],compName);
          getData();
          discardItem();
          ToastUtil.successMsg('Created batch');
        })
        .catch((err) => {
          ToastUtil.error(err);
          data.dataLoading = false;
        })
  }
}

const discardItem = () => {
  data.newBatchName = null;
  data.editedItem = JSON.parse(JSON.stringify(data.defaultItem));
  data.newBatch = false;
  data.isEditing = false;
}

onMounted(()=>{
  getData();
})

</script>

<template>
  <v-container fluid>
    <v-card class="mx-auto">
      <v-toolbar flat color="white">
        <v-toolbar-title>Batches</v-toolbar-title>
        <v-progress-linear
            :active="data.loading"
            :indeterminate="data.loading"
            absolute
            bottom
            color="primary"
        >
        </v-progress-linear>
        <v-divider class="mx-4" inset vertical></v-divider>

        <JsonCSV
            :data="data.items"
            name="Batches.csv"
            :labels="data.labels"
        >
          <v-btn size="small" variant="elevated" color="primary">
            <v-icon> mdi-file-download </v-icon>
          </v-btn>
        </JsonCSV>


        <v-text-field
            class="px-10"
            label="Search"
            type="text"
            v-model="data.search"
            append-icon="mdi-magnify"
        >
        </v-text-field>


        <v-spacer></v-spacer>

        <v-btn
            v-if="!data.newBatch && Permissions.checkPermissions(['payments.create-batch'])"
            dark
            color="primary"
            variant="elevated"
            size="small"
            @click="data.newBatch = true"
        >New Batch</v-btn
        >
      </v-toolbar>

      <v-form ref="form" v-model="data.valid" lazy-validation v-if="data.newBatch">
        <v-row class="px-5">
          <v-col cols="12" sm="6">
            <v-text-field
                type="text"
                v-model="data.newBatchName"
                label="Batch Name"
                :rules="[v => !!v || 'Batch name is required']"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="4">
            <v-btn
                size="small"
                variant="elevated"
                class="px-2"
                @click="saveItem"
                color="primary"
                :disabled="!data.valid"
            >
              <v-icon color="white">mdi-plus</v-icon>
            </v-btn>
            <v-btn size="small" variant="elevated" class="px-2" @click="discardItem" color="error">
              <v-icon color="white">mdi-close</v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </v-form>

      <v-form ref="form" v-model="data.valid" lazy-validation v-if="data.isEditing">
        <v-row class="px-5">
          <v-col cols="12" sm="6">
            <v-text-field
                type="text"
                v-model="data.editedItem.title"
                label="Batch Name"
                :rules="[v => !!v || 'Batch name is required']"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="4">
            <v-btn
                size="small"
                variant="elevated"
                class="px-2"
                @click="editBatches"
                color="primary"
                :disabled="!data.valid"
            >
              Save
            </v-btn>
            <v-btn size="small" variant="elevated" class="px-2" @click="discardItem" color="error">
              Cancel
            </v-btn>
          </v-col>
        </v-row>
      </v-form>

      <v-data-table
          :headers="data.headers"
          :items="data.items"
          :loading="data.dataLoading"
          :search="data.search"
          loading-text="Loading... Please wait"
      >
        <template v-slot:[`item.sum`]="{ item }">
          <span>{{ item.sumFormatted }}</span>
        </template>
        <template v-slot:[`item.isclosed`]="{ item }">
          <span v-if="item.isclosed === 'true' ||  item.isclosed === true">Closed</span>
          <span v-else> Open </span>
        </template>
        <template v-slot:[`item.title`]="{ item }">
          <RouterLink :to="`/batch/${item.id}`">
            {{ item.title }}
          </RouterLink>
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <v-icon v-if="Permissions.checkPermissions(['payments.update-batch'])" @click="editItem(item)"> mdi-pencil </v-icon>
          <v-icon v-if="Permissions.checkPermissions(['payments.delete-batch'])" @click="deleteItem(item)"> mdi-delete </v-icon>
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<style scoped>
.container {
  padding: 5px;
}

[v-cloak] {
  display: none;
}
</style>